import React, { useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Button } from '../../../components/Button/Button'
import PrizeBadge from '../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'
import { TwoWaySwitch } from '../../../components/TwoWaySwitch/TwoWaySwitch'
import { ConsentUploadSetting } from '../../../models/Account'
import { ReactComponent as ChevronBullet } from '../../../assets/images/chevron-bullet.svg'
import { MeetingLevelConsentConfirmModal } from './MeetingLevelConsentConfirmModal'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import './Settings.scss'
import { LinkButton } from '../../../components/Button/LinkButton'
import { SuccessToast } from '../../../components/SuccessToast/SuccessToast'
import { isEnterpiseUser } from '../../../common/userUtils'

const DEFAULT_CONSENT_MESSAGE = 'Confirm that all clients agreed to this meeting being captured by your AI Assistant.'

export const ConsentSettings: React.FC = () => {
  useDocumentTitle('Setup')
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount] = useUpdateAccountMutation()
  const { hasQuota } = useZocksQuota()
  const userConsentTrackingAvailable = hasQuota('ai.userConsentTracking.value')
  const consentApprovalCheckAvailable = hasQuota('room.consentApprovalCheck.value')
  const manualConsentTrackingAvailable = hasQuota('identity.manualConsentTrackingEnabled.value')

  const compliance = account?.data.effectiveConfig.complianceConfig
  const manualConfigs = compliance?.userConsentManualConfigs
  const enterpriseCompliance = account?.data.enterpriseConfig.complianceConfig

  const userConsentTrackingEnabled = compliance?.userConsentTrackingEnabled || false
  const userConsentManualOverride = compliance?.userConsentManualOverride || false
  const consentApprovalCheckEnabled = compliance?.consentApprovalCheck || false
  const userConsentTrackingEnterpriseControlled =
    enterpriseCompliance?.userConsentTrackingEnabled !== null && enterpriseCompliance?.userConsentTrackingEnabled !== undefined
  const consentApprovalCheckEnterpriseControlled = enterpriseCompliance?.consentApprovalCheck != null
  const consentManualConfigOverrideEnterpriseControlled = enterpriseCompliance?.userConsentManualOverride != null
  const consentManualConfigsEnterpriseControlled = enterpriseCompliance?.userConsentManualConfigs != null

  const meetingLevelConsent = manualConfigs?.meetingLevelConsent || false
  const consentWarningTextEnabled = manualConfigs?.consentWarningText.enabled || false
  const consentWarningTextValue = manualConfigs?.consentWarningText.text || ''
  const evidenceUploadEnabled = manualConfigs?.evidenceUpload.enabled || false
  const evidenceUploadSetting: ConsentUploadSetting = manualConfigs?.evidenceUpload.setting || 'optional'
  const commentUploadEnabled = manualConfigs?.commentUpload.enabled || false
  const commentUploadSetting: ConsentUploadSetting = manualConfigs?.commentUpload.setting || 'optional'

  const [consentMessageDraft, setConsentMessageDraft] = useState<string | null>(null)
  const [meetingLevelConsentConfirmPending, setMeetingLevelConsentConfirmPending] = useState<'everyMeeting' | 'perClient' | null>(null)
  const [showDownloadToast, setShowDownloadToast] = useState(false)

  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const isDownloadEnabled = isEnterpiseUser(authenticatedUser)

  const handleExportConsentConfig = () => {
    if (!account) return

    const config = account.data.config
    const effectiveCompliance = account.data.effectiveConfig.complianceConfig
    const consentManualConfigs = config.userConsentManualConfigs

    const exportData = {
      userConsentTrackingEnabled: effectiveCompliance.userConsentTrackingEnabled ?? false,
      userConsentManualOverride: effectiveCompliance.userConsentManualOverride ?? false,
      consentApprovalCheck: effectiveCompliance.consentApprovalCheck ?? false,
      userConsentManualConfigs: {
        consentWarningText: {
          enabled: consentManualConfigs.consentWarningText.enabled,
          text: consentManualConfigs.consentWarningText.text,
        },
        evidenceUpload: {
          enabled: consentManualConfigs.evidenceUpload.enabled,
          setting: consentManualConfigs.evidenceUpload.setting,
        },
        commentUpload: {
          enabled: consentManualConfigs.commentUpload.enabled,
          setting: consentManualConfigs.commentUpload.setting,
        },
        meetingLevelConsent: consentManualConfigs.meetingLevelConsent,
      },
    }

    const blob = new Blob([JSON.stringify(exportData, null, 4)], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'consent_settings.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setShowDownloadToast(true)
  }

  const currentMessageText = consentMessageDraft ?? consentWarningTextValue
  const isMessageDirty = consentMessageDraft !== null && consentMessageDraft !== consentWarningTextValue

  const buildManualConfigs = (overrides: object) => ({
    ...account!.data.config.userConsentManualConfigs,
    ...overrides,
  })

  const userConsentTrackingChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({ accountId, payload: { config: { ...account.data.config, userConsentTrackingEnabled: checked } } })
  }

  const consentApprovalCheckChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({ accountId, payload: { config: { ...account.data.config, consentApprovalCheck: checked } } })
  }

  const userConsentManualOverrideChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({ accountId, payload: { config: { ...account.data.config, userConsentManualOverride: checked } } })
  }

  const meetingLevelConsentChanged = (option: string) => {
    const targetOption = option === 'Per every meeting' ? 'everyMeeting' : 'perClient'
    setMeetingLevelConsentConfirmPending(targetOption)
  }

  const handleMeetingLevelConsentConfirm = () => {
    if (!account || meetingLevelConsentConfirmPending === null) return
    const value = meetingLevelConsentConfirmPending === 'everyMeeting'
    setMeetingLevelConsentConfirmPending(null)
    updateAccount({
      accountId,
      payload: { config: { ...account.data.config, userConsentManualConfigs: buildManualConfigs({ meetingLevelConsent: value }) } },
    })
  }

  const handleMeetingLevelConsentCancel = () => {
    setMeetingLevelConsentConfirmPending(null)
  }

  const consentWarningTextEnabledChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({
            consentWarningText: { ...account.data.config.userConsentManualConfigs.consentWarningText, enabled: checked },
          }),
        },
      },
    })
  }

  const handleCancelMessage = () => {
    setConsentMessageDraft(null)
  }

  const handleResetToDefault = () => {
    setConsentMessageDraft(DEFAULT_CONSENT_MESSAGE)
  }

  const handleSaveMessage = async () => {
    if (!account || consentMessageDraft === null) return
    const result = await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({
            consentWarningText: { ...account.data.config.userConsentManualConfigs.consentWarningText, text: consentMessageDraft },
          }),
        },
      },
    })
    if (!('error' in result)) {
      setConsentMessageDraft(null)
    }
  }

  const evidenceUploadEnabledChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({
            evidenceUpload: { ...account.data.config.userConsentManualConfigs.evidenceUpload, enabled: checked },
          }),
        },
      },
    })
  }

  const evidenceUploadSettingChanged = (option: string) => {
    const value = option === 'Required' ? 'required' : ('optional' as ConsentUploadSetting)
    if (!account) return
    updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({ evidenceUpload: { ...account.data.config.userConsentManualConfigs.evidenceUpload, setting: value } }),
        },
      },
    })
  }

  const commentUploadEnabledChanged = (checked: boolean) => {
    if (!account) return
    updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({ commentUpload: { ...account.data.config.userConsentManualConfigs.commentUpload, enabled: checked } }),
        },
      },
    })
  }

  const commentUploadSettingChanged = (option: string) => {
    const value = option === 'Required' ? 'required' : ('optional' as ConsentUploadSetting)
    if (!account) return
    updateAccount({
      accountId,
      payload: {
        config: {
          ...account.data.config,
          userConsentManualConfigs: buildManualConfigs({ commentUpload: { ...account.data.config.userConsentManualConfigs.commentUpload, setting: value } }),
        },
      },
    })
  }

  return (
    <div>
      <SuccessToast position='bottom-center' message='Consent configuration exported.' show={showDownloadToast} onClose={() => setShowDownloadToast(false)} />
      {meetingLevelConsentConfirmPending ? (
        <MeetingLevelConsentConfirmModal
          show
          targetOption={meetingLevelConsentConfirmPending}
          onConfirm={handleMeetingLevelConsentConfirm}
          onHide={handleMeetingLevelConsentCancel}
        />
      ) : null}
      <Row className='d-flex gap-3 align-items-center px-2 pb-3 m-0'>
        <Col xs='auto' className='p-0 d-flex align-items-center'>
          <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
            <Tooltip title={userConsentTrackingEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
              <Form.Switch
                disabled={!userConsentTrackingAvailable || userConsentTrackingEnterpriseControlled}
                checked={userConsentTrackingEnabled}
                onChange={(e) => userConsentTrackingChanged(e.currentTarget.checked)}
              />
            </Tooltip>
          </ScopedBlock>
        </Col>
        <Col className='p-0 d-flex flex-column'>
          <div className='fw-bold paragraph-medium zocks-text-gray-900'>Track client consent</div>
          <div className='paragraph-small zocks-text-gray-600'>
            Keep a record of client consent for using Zocks to process meetings. Consent is tracked by the AI Assistant throughout meetings. Status history is
            visible on the Contact's page.
          </div>
        </Col>
        {!userConsentTrackingAvailable ? (
          <Col xs='auto' className='p-0'>
            <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
              <LinkButton className='d-flex p-1 p-2 align-items-center text-nowrap' variant='primary' to='/account/plan'>
                <img alt='Upgrade' src={PrizeBadge} />
                Get feature
              </LinkButton>
            </ScopedBlock>
          </Col>
        ) : null}
        {isDownloadEnabled ? (
          <Col xs='auto' className='p-0'>
            <OverlayTrigger placement='bottom' overlay={<BootstrapTooltip id='export-consent-tooltip'>Export consent configuration</BootstrapTooltip>}>
              {({ ref, ...triggerHandler }) => (
                <Button onClick={handleExportConsentConfig} {...triggerHandler}>
                  <i ref={ref} className='bi bi-download' />
                </Button>
              )}
            </OverlayTrigger>
          </Col>
        ) : null}
      </Row>

      {userConsentTrackingEnabled ? (
        <>
          <Row className='d-flex gap-3 align-items-center border-top pt-3 ps-3 pe-3 m-0 pb-3'>
            <Col xs='auto' className='p-0 d-flex align-items-start pt-1'>
              <ChevronBullet />
            </Col>
            <Col xs='auto' className='d-flex align-items-center p-0'>
              <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
                <ScopedBlock
                  quota={{ quota: 'room.consentApprovalCheck.value' }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                  upgradeContainerClassName='d-flex'
                >
                  <Tooltip title={consentApprovalCheckEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
                    <Form.Switch
                      disabled={!consentApprovalCheckAvailable || consentApprovalCheckEnterpriseControlled}
                      checked={consentApprovalCheckEnabled}
                      onChange={(e) => consentApprovalCheckChanged(e.currentTarget.checked)}
                    />
                  </Tooltip>
                </ScopedBlock>
              </ScopedBlock>
            </Col>
            <Col className='p-0 d-flex flex-column'>
              <div className='fw-bold paragraph-medium zocks-text-gray-900'>Lock past meetings until consent is given</div>
              <div className='paragraph-small zocks-text-gray-600'>Restrict access to past meeting details until all clients have given consent.</div>
            </Col>
            <Col xs='auto' className='p-0 text-nowrap'>
              <TwoWaySwitch
                leftOption='Per client'
                rightOption='Per every meeting'
                selectedOption={meetingLevelConsent ? 'Per every meeting' : 'Per client'}
                onChange={meetingLevelConsentChanged}
                customClassName={'two-way-switch-options paragraph-medium'}
                disabled={!consentApprovalCheckEnabled || consentApprovalCheckEnterpriseControlled}
              />
            </Col>
          </Row>
          {consentApprovalCheckEnabled && !userConsentManualOverride ? (
            <Row className='d-flex gap-3 m-0 ps-3 pe-3 pb-3'>
              <Col xs='auto' className='p-0 invisible'>
                <ChevronBullet />
              </Col>
              <Col xs='auto' className='p-0 invisible'>
                <Form.Switch />
              </Col>
              <Col className='p-0'>
                <div className='d-flex align-items-center gap-2 zocks-bg-color-primary-light rounded p-2'>
                  <i className='bi bi-info-circle zocks-text-primary flex-shrink-0' />
                  <span className='paragraph-small zocks-text-gray-900'>Turn on "Manage consent manually" to keep the option to unlock meetings later.</span>
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className='d-flex gap-3 align-items-center border-top py-3 ps-3 pe-3 m-0'>
            <Col xs='auto' className='p-0 d-flex align-items-start pt-1'>
              <ChevronBullet />
            </Col>
            <Col xs='auto' className='p-0 d-flex align-items-center'>
              <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
                <Tooltip title={consentManualConfigOverrideEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
                  <Form.Switch
                    disabled={!userConsentTrackingAvailable || consentManualConfigOverrideEnterpriseControlled || !manualConsentTrackingAvailable}
                    checked={userConsentManualOverride}
                    onChange={(e) => userConsentManualOverrideChanged(e.currentTarget.checked)}
                  />
                </Tooltip>
              </ScopedBlock>
            </Col>
            <Col className='p-0 d-flex flex-column'>
              <div className='fw-bold paragraph-medium zocks-text-gray-900'>Manage consent manually</div>
              <div className='paragraph-small zocks-text-gray-600'>Set or override client consent at any time.</div>
            </Col>
            <Col xs='auto' className='p-0 ms-auto'>
              {!manualConsentTrackingAvailable ? (
                <ScopedBlock
                  quota={{
                    quota: 'identity.manualConsentTrackingEnabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <LinkButton variant='primary' to='/account/plan/' target='_blank'>
                    Upgrade
                  </LinkButton>
                </ScopedBlock>
              ) : null}
            </Col>
          </Row>
          {!userConsentManualOverride && manualConsentTrackingAvailable ? (
            <ScopedBlock scope={{ scope: 'consent_management:admin', superScope: [] }} missingScopeAction={MissingScopeAction.HIDE}>
              <Row className='m-0 ps-3 pe-3 gap-3'>
                <Col xs='auto' className='p-0 invisible'>
                  <ChevronBullet />
                </Col>
                <Col xs='auto' className='p-0 invisible'>
                  <Form.Switch />
                </Col>
                <Col className='p-0 '>
                  <div className='d-flex align-items-center gap-2 zocks-bg-color-primary-light rounded p-2'>
                    <i className='bi bi-info-circle zocks-text-primary flex-shrink-0' />
                    <span className='paragraph-small zocks-text-gray-900'>You have permission to manage client consent manually, even when it's disabled</span>
                  </div>
                </Col>
              </Row>
            </ScopedBlock>
          ) : null}

          {userConsentManualOverride && manualConsentTrackingAvailable ? (
            <div className='ps-5'>
              <Row className='d-flex gap-3 align-items-center border-top py-3 ps-3 pe-3 m-0'>
                <Col xs='auto' className='p-0 d-flex align-items-start pt-1'>
                  <ChevronBullet />
                </Col>
                <Col xs='auto' className='p-0 d-flex align-items-center'>
                  <Tooltip title={consentManualConfigsEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
                    <Form.Switch
                      disabled={consentManualConfigsEnterpriseControlled}
                      checked={consentWarningTextEnabled}
                      onChange={(e) => consentWarningTextEnabledChanged(e.currentTarget.checked)}
                    />
                  </Tooltip>
                </Col>
                <Col className='p-0 d-flex flex-column'>
                  <div className='fw-bold paragraph-medium zocks-text-gray-900'>Lock screen consent message</div>
                  <div className='paragraph-small zocks-text-gray-600'>Show advisors a custom message when one or more clients haven't given consent yet.</div>
                </Col>
              </Row>
              {consentWarningTextEnabled ? (
                <div className='px-3 pb-3'>
                  <div className='d-flex flex-column gap-2 ps-5 pt-3'>
                    <div className='d-flex flex-column gap-1'>
                      <label className='paragraph-medium zocks-text-gray-900'>Message</label>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        value={currentMessageText}
                        onChange={(e) => setConsentMessageDraft(e.target.value)}
                        className='paragraph-small'
                        style={{ resize: 'none' }}
                      />
                    </div>
                    <div className='d-flex gap-2 justify-content-between align-items-center'>
                      <Button
                        variant='link'
                        className='p-1 zocks-text-primary button-small fw-bold text-decoration-none'
                        disabled={currentMessageText === DEFAULT_CONSENT_MESSAGE}
                        onClick={handleResetToDefault}
                      >
                        Reset to default
                      </Button>
                      <div className='d-flex gap-2'>
                        <Button variant='grayDarkGhostButton' disabled={!isMessageDirty} onClick={handleCancelMessage} className='zocks-py-0.25 zocks-px-0.75'>
                          Cancel
                        </Button>
                        <Button variant='primary' disabled={!isMessageDirty} onClick={handleSaveMessage} className='zocks-py-0.25 zocks-px-0.75'>
                          Save changes
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <Row className='d-flex gap-3 align-items-center border-top py-3 ps-3 pe-3 m-0'>
                <Col xs='auto' className='p-0 d-flex align-items-start pt-1'>
                  <ChevronBullet />
                </Col>
                <Col xs='auto' className='p-0 d-flex align-items-center'>
                  <Tooltip title={consentManualConfigsEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
                    <Form.Switch
                      disabled={consentManualConfigsEnterpriseControlled}
                      checked={evidenceUploadEnabled}
                      onChange={(e) => evidenceUploadEnabledChanged(e.currentTarget.checked)}
                    />
                  </Tooltip>
                </Col>
                <Col className='p-0 d-flex flex-column'>
                  <div className='fw-bold paragraph-medium zocks-text-gray-900'>Upload consent evidence</div>
                  <div className='paragraph-small zocks-text-gray-600'>Upload supporting files up to 5 MB. Files can be viewed from consent history later.</div>
                </Col>
                <Col xs='auto' className='p-0'>
                  <TwoWaySwitch
                    leftOption='Optional'
                    rightOption='Required'
                    selectedOption={evidenceUploadSetting === 'required' ? 'Required' : 'Optional'}
                    onChange={evidenceUploadSettingChanged}
                    customClassName={'two-way-switch-options paragraph-medium'}
                    disabled={!evidenceUploadEnabled || consentManualConfigsEnterpriseControlled}
                  />
                </Col>
              </Row>

              <Row className='d-flex gap-3 align-items-center border-top py-3 ps-3 pe-3 m-0'>
                <Col xs='auto' className='p-0 d-flex align-items-start pt-1'>
                  <ChevronBullet />
                </Col>
                <Col xs='auto' className='p-0 d-flex align-items-center'>
                  <Tooltip title={consentManualConfigsEnterpriseControlled ? 'Your enterprise admin has disabled this setting.' : undefined}>
                    <Form.Switch
                      disabled={consentManualConfigsEnterpriseControlled}
                      checked={commentUploadEnabled}
                      onChange={(e) => commentUploadEnabledChanged(e.currentTarget.checked)}
                    />
                  </Tooltip>
                </Col>
                <Col className='p-0 d-flex flex-column'>
                  <div className='fw-bold paragraph-medium zocks-text-gray-900'>Add comments</div>
                  <div className='paragraph-small zocks-text-gray-600'>Add context about how consent was captured.</div>
                </Col>
                <Col xs='auto' className='p-0'>
                  <TwoWaySwitch
                    leftOption='Optional'
                    rightOption='Required'
                    selectedOption={commentUploadSetting === 'required' ? 'Required' : 'Optional'}
                    onChange={commentUploadSettingChanged}
                    customClassName={'two-way-switch-options paragraph-medium'}
                    disabled={!commentUploadEnabled || consentManualConfigsEnterpriseControlled}
                  />
                </Col>
              </Row>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
